import React, { useCallback, useMemo } from 'react'
import {
  useQueryState,
  useSiteContext,
  useTranslations,
} from '@dustin-web/microsite-components/src/hooks'
import Tag from '@dustin-web/microsite-components/src/tag'
import { IconName } from '@dustin-web/microsite-components/src/icon'
import { BOOLEAN_FACET_NAME, FacetGroup, FacetType } from '.'
import classnames from 'classnames'
import { Button } from '@dustin-web/microsite-components/src/button'

type Props = {
  facetGroups: FacetGroup[]
  mobile?: boolean
  onClearFilter?: () => void
}

type Pill = {
  icon: IconName
  displayGroupName: string
  displayValue: string
  group: string
  type: FacetType
  value: string
}

export const ActiveFilters = ({ facetGroups, mobile = false, onClearFilter }: Props) => {
  const { translate } = useTranslations()
  const { queryState, setQueryState } = useQueryState()
  const { activeFilters } = queryState
  const { isMobile } = useSiteContext()

  const hasActiveFilters = activeFilters.length > 0

  const pills = useMemo(
    () =>
      activeFilters.reduce((pillAcc, activeFilter) => {
        const facetGroup = facetGroups.find(fg => fg.nameInUrl === activeFilter.name)

        if (!facetGroup) {
          return pillAcc
        }
        const icon = 'close'
        const displayGroupName = facetGroup.isTranslated
          ? facetGroup.displayName
          : translate('FacetGroup_' + facetGroup.nameInUrl)

        let addPills: Pill[]
        if (facetGroup.type === FacetType.Range) {
          const values = activeFilter.values
          const value = values[0]
          const minValues = values.map(x => parseInt(x.split('-')[0]))
          const maxValues = values.map(x => parseInt(x.split('-')[1]))

          const min = Math.min(...minValues)
          const max = Math.max(...maxValues)
          const displayValue = facetGroup.unitText
            ? `${min} - ${max} ${facetGroup.unitText}`
            : `${min} - ${max}`

          addPills = [
            {
              icon,
              displayGroupName,
              displayValue,
              group: facetGroup.nameInUrl,
              type: facetGroup.type,
              value,
            },
          ]
        } else {
          addPills = activeFilter.values
            .map(value => {
              const valueItem = facetGroup.values.find(v => v.name.toLocaleLowerCase() === value.toLocaleLowerCase())
              if (!valueItem) {
                return null
              }

              const displayValue = facetGroup.unitText
                ? `${valueItem.displayName ?? ''} ${facetGroup.unitText}`
                : valueItem.displayName

              return {
                icon,
                displayGroupName,
                displayValue,
                group: facetGroup.nameInUrl,
                type: facetGroup.type,
                value: valueItem.name,
              }
            })
            .filter(Boolean) as Pill[]
        }
        return [...pillAcc, ...addPills]
      }, Array<Pill>()),
    [JSON.stringify(activeFilters)]
  )

  const removeFilter = useCallback(
    (pill: Pill) => {
      const pillFilter = activeFilters.find(af => af.name === pill.group)

      if (!pillFilter) {
        return
      }

      if (pill.type === FacetType.Boolean) {
        const otherBooleanFacetGroups = facetGroups.filter(
          fg => fg.type === FacetType.Boolean && fg.nameInUrl !== pill.group
        )

        const newValues = activeFilters
          .filter(af => otherBooleanFacetGroups.find(fg => fg.nameInUrl === af.name))
          .map(af => af.name)

        const keys = {
          [BOOLEAN_FACET_NAME]: newValues.join(','),
          page: '',
        }

        setQueryState(keys, false)
      } else if (pill.type === FacetType.Range) {
        const keys = {
          [pillFilter.name]: '',
          page: '',
        }

        setQueryState(keys, false)
      } else {
        const newValues = pillFilter.values.filter(v => v !== pill.value)

        const keys = {
          [pillFilter.name]: newValues.join(','),
          page: '',
        }

        setQueryState(keys, false)
      }
    },
    [JSON.stringify(activeFilters)]
  )

  return (
    <div
      className={classnames({
        'u-pt-8': hasActiveFilters,
      })}>
      {hasActiveFilters && (
        <>
          {pills.map(pill => {
            return (
              <Tag
                key={`${pill.group}:${pill.value}`}
                className={mobile ? 'u-bg-gray-02' : ''}
                onClick={() => removeFilter(pill)}
                text={pill.type === FacetType.Boolean ? pill.displayGroupName : pill.displayValue}
                variant="dismissible"
              />
            )
          })}
          {!isMobile && onClearFilter && (
            <Button
              defaultBtn
              primary={false}
              text={translate('ResetAllFilters')}
              className="u-mb-8 u-w-auto u-px-12 u-py-8 button-stripped"
              onClick={() => {
                onClearFilter && onClearFilter()
              }}></Button>
          )}
        </>
      )}
    </div>
  )
}
